import { createSlice } from '@reduxjs/toolkit';
import { SurfaceType } from 'src/types/surface';

export type SurfaceSliceState = {
    sku: string; // Should contain the MCP Information
    skuVersion: number | undefined; // Should contain the MCP Information
    variables: Record<string, string>; // Should contain the MCP Information

    merchantSku: string | undefined; // Should contain the Variant (PRD) Information
    merchantVersion: number | undefined; // Should contain the Variant (PRD) Information
    merchantVariables: Record<string, string>; // Should contain the Variant (PRD) Information

    lastFailedSku: string | undefined;
    isResolved: boolean;
    isReady: boolean;
    list: SurfaceType[];
    error?: Error;
    /** Identity of the sku/version/merchantVariables combination the current (or in-flight)
     * `list` fetch applies to — mirrors `decorationSpecs.cacheKey`. `changeSkuForEditor` (on
     * `changeEditorSku`) and `getSurfaces` (on `applyVariables`) both fetch and dispatch
     * `retrieveSurfacesSuccess` independently; without this, whichever of the two — or two
     * overlapping runs of the same one — happens to resolve *last* wins unconditionally, even if
     * it started from an older selection. `retrieveSurfacesSuccess` only applies when this still
     * matches the key it was fetched for. */
    surfacesFetchKey: string | undefined;
};

export const initialState: SurfaceSliceState = {
    sku: '',
    skuVersion: undefined,
    variables: {},

    merchantSku: undefined,
    merchantVersion: undefined,
    merchantVariables: {},

    lastFailedSku: undefined,
    isResolved: false,
    isReady: false,
    list: [],
    surfacesFetchKey: undefined,
};

const slice = createSlice({
    name: 'surfaces',
    initialState,
    reducers: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        validateEditorSku: (_state, { payload }) => {},
        validateEditorSkuSuccess: (state, { payload }) => {
            state.sku = payload.sku;
            state.skuVersion = payload.skuVersion;
            state.variables = payload.variables;
            state.merchantSku = payload.merchantSku;
            state.merchantVersion = payload.merchantVersion;
            state.merchantVariables = payload.merchantVariables;
        },
        validateEditorSkuFailed: (state, { payload }) => {
            state.lastFailedSku = payload.sku;
            state.sku = '';
            state.skuVersion = 0;
            state.variables = {};
            state.merchantSku = undefined;
            state.merchantVersion = undefined;
            state.merchantVariables = {};
        },
        /* eslint-disable @typescript-eslint/no-unused-vars */
        changeEditorSku: (_state, { payload }) => {},
        changeEditorSkuVersion: (state, { payload }) => {
            state.merchantVersion = payload;
        },
        /* eslint-disable @typescript-eslint/no-unused-vars */
        clearEditorSku: (state, { payload }) => {
            state.sku = payload || '';
            state.skuVersion = undefined;
            state.variables = {};
            state.merchantSku = undefined;
            state.merchantVersion = undefined;
            state.merchantVariables = {};
            state.isResolved = false;
            state.isReady = false;
            state.list = [];
        },
        changeEditorSkuSuccess: (state) => {
            state.variables = {};
            state.list = [];
            state.isReady = true;
        },
        changeEditorSkuFailed: (state, { payload }) => {
            state.error = {
                ...payload,
            };
        },
        applyVariables: (state, { payload }) => {
            if (JSON.stringify(state.merchantVariables) !== JSON.stringify(payload.variables)) {
                state.merchantVariables = payload.variables;
                state.isResolved = payload.resolved;
            }
        },
        /** Claims `surfacesFetchKey` for a surfaces fetch about to start, set optimistically
         * before the request resolves — mirrors `decorationSpecs`'s `retrieveDecorationSpecs`.
         * Dispatched by both `changeSkuForEditor` and `getSurfaces`, each computing the key from
         * the sku/version/merchantVariables selection they read at the start of their own run. */
        requestSurfaces: (state, { payload }) => {
            state.surfacesFetchKey = payload.surfacesFetchKey;
        },
        retrieveSurfacesSuccess: (state, { payload }) => {
            // Guard against a slower, now-superseded fetch (either producer, from either saga)
            // resolving after a newer selection already claimed `surfacesFetchKey` for itself.
            if (state.surfacesFetchKey !== payload.surfacesFetchKey) {
                return;
            }
            state.list = payload.surfaces;
            state.isResolved = true;
        },
        retrieveSurfacesFailed: () => {},
        unresolveVariables: (state, { payload }) => {
            state.variables = payload;
            state.isResolved = false;
        },
    },
});

export const reducer = slice.reducer;

export const {
    changeEditorSku,
    changeEditorSkuVersion,
    clearEditorSku,
    changeEditorSkuSuccess,
    changeEditorSkuFailed,
    validateEditorSku,
    validateEditorSkuSuccess,
    validateEditorSkuFailed,
    applyVariables,
    requestSurfaces,
    retrieveSurfacesSuccess,
    retrieveSurfacesFailed,
    unresolveVariables,
} = slice.actions;
